import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { dashboardsAPI, metricsAPI } from '../services/api';

const CompanySelector = () => {
  const navigate = useNavigate();
  const companies = useStore((state) => state.companies);
  const currentCompany = useStore((state) => state.currentCompany);
  const setCurrentCompany = useStore((state) => state.setCurrentCompany);
  const setDashboards = useStore((state) => state.setDashboards);
  const setCurrentDashboard = useStore((state) => state.setCurrentDashboard);
  const setMetrics = useStore((state) => state.setMetrics);
  const setMetricDefinitions = useStore((state) => state.setMetricDefinitions);
  const setDashboardLastUpdate = useStore((state) => state.setDashboardLastUpdate);
  const dashboardLastUpdates = useStore((state) => state.dashboardLastUpdates);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanyChange = async (company) => {
    setIsOpen(false);

    // Skip if already on this company
    if (currentCompany?.id === company.id) {
      return;
    }

    // Set company first
    setCurrentCompany(company);

    // Clear current dashboard to show loading state
    setCurrentDashboard(null);

    try {
      // Load all dashboards for this company
      const dashboardsResponse = await dashboardsAPI.getAll();
      if (dashboardsResponse.success) {
        // Filter dashboards for this company
        const companyDashboards = dashboardsResponse.data.filter(d => d.company_id === company.id);
        setDashboards(companyDashboards);

        // Find the default or first dashboard
        const dashboardToLoad = companyDashboards.find(d => d.is_default) || companyDashboards[0];
        if (dashboardToLoad) {
          // Load the full dashboard data (including widgets) by ID
          const fullDashboardResponse = await dashboardsAPI.getById(dashboardToLoad.id);
          if (fullDashboardResponse.success) {
            setCurrentDashboard(fullDashboardResponse.data);
          }
        } else {
          // No dashboards available for this company - clear current dashboard
          setCurrentDashboard(null);
        }
      }

      // Load metrics for this company
      const metricsResponse = await metricsAPI.getLatestAll(company.id);
      if (metricsResponse.success) {
        const metricsMap = {};
        let mostRecentTimestamp = null;

        metricsResponse.data.forEach((metric) => {
          metricsMap[metric.metric_key] = metric;

          // Track the most recent timestamp from all metrics
          if (metric.timestamp) {
            const metricTime = new Date(metric.timestamp);
            if (!mostRecentTimestamp || metricTime > mostRecentTimestamp) {
              mostRecentTimestamp = metricTime;
            }
          }
        });

        setMetrics(metricsMap);

        // Only set initial timestamp for the dashboard if it doesn't already have one
        // This prevents overwriting timestamps when switching companies/dashboards
        // Real-time updates will come from WebSocket
        const loadedDashboard = companyDashboards.find(d => d.is_default) || companyDashboards[0];
        if (mostRecentTimestamp && loadedDashboard) {
          const currentTimestamp = dashboardLastUpdates[loadedDashboard.id];
          if (!currentTimestamp) {
            setDashboardLastUpdate(loadedDashboard.id, mostRecentTimestamp);
          }
        }
      }

      // Load metric definitions for this company
      const definitionsResponse = await metricsAPI.getDefinitions(company.id);
      if (definitionsResponse.success) {
        setMetricDefinitions(definitionsResponse.data);
      }

      // Navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Error switching company:', error);
      // Fallback to reload if there's an error
      window.location.reload();
    }
  };

  // Don't show selector if user has no companies
  if (!companies || companies.length === 0) {
    return null;
  }

  return (
    <div className="company-selector" ref={dropdownRef}>
      <button
        className="company-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="selector-icon">🏢</span>
        <span className="company-name">
          {currentCompany?.name || 'Select Company'}
        </span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="company-dropdown">
          {companies.map((company) => (
            <button
              key={company.id}
              className={`company-option ${
                currentCompany?.id === company.id ? 'active' : ''
              }`}
              onClick={() => handleCompanyChange(company)}
            >
              <span className="company-option-name">{company.name}</span>
              {currentCompany?.id === company.id && (
                <span className="checkmark">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
